/**
 * Block Renderer - Maps block types to React components
 */

import React from "react";
import type { BlockGridItem } from "@/types/content";
import {
  isHeroMediaShowcaseBlock,
  isRichTextBlock,
  isImageTextOverlayCardBlock,
  isSpacerBlock,
  isProductCardBlock,
  isLargeImageBlock,
  isProductListBlock,
  isIconTextBlock,
  isBentoQuoteBlock,
  isTestimonialsBlock,
  isArticleListTeaserBlock,
  isArticlesListingBlock,
  isNewsletterSignupBlock,
  isCartItemsBlock,
  isCartSummaryBlock,
  isProductImageGalleryBlock,
  isProductDetailsBlock,
  isProductDetailContentBlock,
  isPricingTableBlock,
  isMediaGalleryBlock,
  isTabbedFeatureBlock,
  isInstagramFeedBlock,
  isContentCarouselBlock,
  isFeatureComparisonBlock,
  isStyledRowBlock,
  isFaqBlock,
  isCategoryHeaderBlock,
} from "@/types/content";
import { HeroMediaShowcase } from "@/components/content/HeroMediaShowcase";
import { RichText } from "@/components/content/RichText";
import { ImageTextOverlayCard } from "@/components/content/ImageTextOverlayCard";
import { Spacer } from "@/components/content/Spacer";
import { ProductCard } from "@/components/content/ProductCard";
import { LargeImage } from "@/components/content/LargeImage";
import { ProductListSection } from "@/components/content/ProductListSection";
import { IconTextBlock } from "@/components/content/IconTextBlock";
import { BentoQuoteBlock } from "@/components/content/QuoteBlock";
import { TestimonialsSection } from "@/components/content/TestimonialsSection";
import { ArticleListSection } from "@/components/content/ArticleListSection";
import { ArticlesListingBlock } from "@/components/content/ArticlesListingBlock";
import { NewsletterSignup } from "@/components/content/NewsletterSignup";
import { CartItems } from "@/components/content/CartItems";
import { CartSummary } from "@/components/content/CartSummary";
import { ProductImageGalleryBlock } from "@/components/content/ProductImageGalleryBlock";
import { ProductDetails } from "@/components/content/ProductDetails";
import { ProductDetailContent } from "@/components/content/ProductDetailContent";
import { PricingTable } from "@/components/content/PricingTable";
import { MediaGallery } from "@/components/content/MediaGallery";
import { TabbedFeature } from "@/components/content/TabbedFeature";
import { InstagramFeed } from "@/components/content/InstagramFeed";
import { ContentCarousel } from "@/components/content/ContentCarousel";
import { FeatureComparison } from "@/components/content/FeatureComparison";
import { StyledRow } from "@/components/content/StyledRow";
import { FaqSection } from "@/components/content/ProductDetailContent/blocks/FaqSection";
import { CategoryHeader } from "@/components/content/CategoryHeader";

export interface BlockRendererProps {
  item: BlockGridItem;
  /** Area children passed from BlockGrid for layout blocks (blocks with areas) */
  areaChildren?: React.ReactNode;
}

/**
 * Renders the appropriate component for a block grid item based on its content type
 */
export const BlockRenderer: React.FC<BlockRendererProps> = ({
  item,
  areaChildren,
}) => {
  const { content } = item;

  if (!content) {
    return null;
  }

  // Map content types to components
  if (isHeroMediaShowcaseBlock(content)) {
    return <HeroMediaShowcase block={content} />;
  }

  if (isRichTextBlock(content)) {
    return <RichText block={content} />;
  }

  if (isImageTextOverlayCardBlock(content)) {
    return <ImageTextOverlayCard block={content} />;
  }

  if (isSpacerBlock(content)) {
    return <Spacer block={content} />;
  }

  if (isProductCardBlock(content)) {
    return <ProductCard block={content} />;
  }

  if (isLargeImageBlock(content)) {
    return <LargeImage block={content} />;
  }

  if (isProductListBlock(content)) {
    return <ProductListSection block={content} />;
  }

  if (isIconTextBlock(content)) {
    return <IconTextBlock block={content} />;
  }

  if (isBentoQuoteBlock(content)) {
    return <BentoQuoteBlock block={content} />;
  }

  if (isTestimonialsBlock(content)) {
    return <TestimonialsSection block={content} />;
  }

  if (isArticleListTeaserBlock(content)) {
    return <ArticleListSection block={content} />;
  }

  if (isArticlesListingBlock(content)) {
    return <ArticlesListingBlock block={content} />;
  }

  if (isNewsletterSignupBlock(content)) {
    return <NewsletterSignup block={content} />;
  }

  if (isCartItemsBlock(content)) {
    return <CartItems block={content} />;
  }

  if (isCartSummaryBlock(content)) {
    return <CartSummary block={content} />;
  }

  if (isProductImageGalleryBlock(content)) {
    return <ProductImageGalleryBlock block={content} />;
  }

  if (isProductDetailsBlock(content)) {
    return <ProductDetails />;
  }

  if (isProductDetailContentBlock(content)) {
    return <ProductDetailContent />;
  }

  if (isPricingTableBlock(content)) {
    return <PricingTable block={content} />;
  }

  if (isMediaGalleryBlock(content)) {
    return <MediaGallery block={content} />;
  }

  if (isTabbedFeatureBlock(content)) {
    return <TabbedFeature block={content} />;
  }

  if (isInstagramFeedBlock(content)) {
    return <InstagramFeed block={content} />;
  }

  if (isContentCarouselBlock(content)) {
    return <ContentCarousel block={content} />;
  }

  if (isFeatureComparisonBlock(content)) {
    return <FeatureComparison block={content} />;
  }

  if (isStyledRowBlock(content)) {
    return <StyledRow block={content}>{areaChildren}</StyledRow>;
  }

  if (isFaqBlock(content)) {
    return <FaqSection block={content} />;
  }

  if (isCategoryHeaderBlock(content)) {
    return <CategoryHeader block={content} />;
  }

  // Fallback for unknown block types - show placeholder in development
  if (import.meta.env.DEV) {
    return (
      <div
        style={{
          padding: "2rem",
          background: "#f5f5f5",
          border: "2px dashed #ccc",
          borderRadius: "8px",
          textAlign: "center",
        }}
      >
        <p style={{ margin: 0, color: "#666" }}>
          Unknown block type: <code>{content.type}</code>
        </p>
      </div>
    );
  }

  return null;
};

/**
 * Render function for use with BlockGrid component
 */
// eslint-disable-next-line react-refresh/only-export-components
export function renderBlock(
  item: BlockGridItem,
  areaChildren?: React.ReactNode
): React.ReactNode {
  return <BlockRenderer item={item} areaChildren={areaChildren} />;
}

export default BlockRenderer;
